import { useCallback, useEffect, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css';
import { CheckCircleIcon, LockClosedIcon } from '@heroicons/react/24/solid';
import api from '../services/api';

const nodeWidth = 280;
const nodeHeight = 120;

// Auto-layout using dagre
const getLayoutedElements = (nodes, edges, direction = 'LR') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ 
    rankdir: direction,
    nodesep: 120,       // Vertical spacing between nodes
    ranksep: 250,       // Horizontal spacing between levels
    edgesep: 80,        // Spacing between edges
    marginx: 80,
    marginy: 80,
    ranker: 'longest-path',  // Better for showing dependencies
    align: 'UL',        // Align to upper-left for better spacing
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    
    return {
      ...node,
      targetPosition: 'left',
      sourcePosition: 'right',
      // We are shifting the dagre node position (anchor=center center) to the top left
      // so it matches the React Flow node anchor point (top left).
      position: {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

// Custom Node Component
const CustomNode = ({ data }) => {
  const { code, title, completed, is_locked, completion_percentage } = data;

  if (is_locked) {
    return (
      <div className="group relative">
        <Handle type="target" position={Position.Left} style={{ background: '#9ca3af' }} />
        <div className="px-6 py-5 rounded-2xl bg-white border-2 border-gray-200 shadow-sm opacity-60 transition-all duration-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <LockClosedIcon className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-bold text-gray-400">{code}</span>
            </div>
          </div>
          <h4 className="text-sm font-semibold text-gray-400 line-clamp-2 mb-2">{title}</h4>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span>🔒 Prerequisites required</span>
          </div>
        </div>
        <Handle type="source" position={Position.Right} style={{ background: '#9ca3af' }} />
      </div>
    );
  }

  if (completed) {
    return (
      <div className="group relative">
        <Handle type="target" position={Position.Left} style={{ background: '#10b981' }} />
        <div className="px-6 py-5 rounded-2xl bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 border-2 border-emerald-400 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="h-6 w-6 text-emerald-600" />
              <span className="text-sm font-bold text-emerald-900">{code}</span>
            </div>
            <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full">
              ✓ Done
            </span>
          </div>
          <h4 className="text-sm font-semibold text-emerald-900 line-clamp-2 mb-2">{title}</h4>
          <div className="flex items-center gap-2 text-xs text-emerald-600 font-medium">
            <span>🎉 Completed</span>
          </div>
        </div>
        <Handle type="source" position={Position.Right} style={{ background: '#10b981' }} />
      </div>
    );
  }

  // Available/In Progress
  return (
    <div className="group relative">
      <Handle type="target" position={Position.Left} style={{ background: '#6366f1' }} />
      <div className="px-6 py-5 rounded-2xl bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border-2 border-blue-500 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-bold text-blue-900">{code}</span>
          {completion_percentage > 0 ? (
            <span className="px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
              {completion_percentage}%
            </span>
          ) : (
            <span className="px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
              Start
            </span>
          )}
        </div>
        <h4 className="text-sm font-semibold text-blue-900 line-clamp-2 mb-3">{title}</h4>
        
        {completion_percentage > 0 ? (
          <div className="space-y-1">
            <div className="w-full bg-blue-200/50 rounded-full h-2.5 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-full transition-all duration-500 shadow-sm"
                style={{ width: `${completion_percentage}%` }}
              />
            </div>
            <p className="text-xs text-blue-600 font-medium">In Progress</p>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs text-blue-600 font-medium">
            <span>👉 Available to start</span>
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} style={{ background: '#6366f1' }} />
    </div>
  );
};

const nodeTypes = {
  custom: CustomNode,
};

export default function PrerequisiteTree() {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPrerequisiteGraph();
  }, []);

  const fetchPrerequisiteGraph = async () => {
    try {
      setLoading(true);
      const response = await api.get('/prerequisites/graph/me');
      const { nodes: graphNodes, edges: graphEdges } = response.data;

      // Convert to React Flow format
      const flowNodes = graphNodes.map((node) => ({
        id: String(node.id),
        type: 'custom',
        data: {
          code: node.code,
          title: node.title,
          completed: node.completed,
          is_locked: node.is_locked,
          completion_percentage: node.completion_percentage || 0,
        },
        position: { x: 0, y: 0 }, // Will be set by layout
      }));

      // Filter edges to only include those where both nodes exist
      const nodeIds = new Set(flowNodes.map(n => n.id));
      const validEdges = graphEdges.filter(edge => {
        const hasSource = nodeIds.has(String(edge.source));
        const hasTarget = nodeIds.has(String(edge.target));
        return hasSource && hasTarget;
      });

      const flowEdges = validEdges.map((edge, index) => ({
        id: `e${edge.source}-${edge.target}-${index}`,
        source: String(edge.source),
        target: String(edge.target),
        type: 'smoothstep',
        animated: false,
        style: { 
          stroke: '#6366f1', 
          strokeWidth: 3,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: '#6366f1',
          width: 25,
          height: 25,
        },
      }));

      // Apply dagre layout
      const layouted = getLayoutedElements(flowNodes, flowEdges, 'LR');
      
      setNodes(layouted.nodes);
      setEdges(layouted.edges);
    } catch (error) {
      console.error('Error fetching prerequisite graph:', error);
    } finally {
      setLoading(false);
    }
  };

  const onNodeClick = useCallback((event, node) => {
    // Could navigate to module page or show details
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-96 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mb-4"></div>
        <p className="text-sm font-medium text-gray-600">Loading your learning path...</p>
      </div>
    );
  }

  return (
    <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden" style={{ height: '900px' }}>
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 z-20">
        <h3 className="text-white font-bold text-lg flex items-center gap-2">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          Your Learning Journey
        </h3>
        <p className="text-indigo-100 text-sm mt-1">Follow the path from left to right • Complete prerequisites to unlock new modules</p>
      </div>

      <div className="h-full pt-24">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{
            padding: 0.15,
            includeHiddenNodes: false,
            minZoom: 0.3,
            maxZoom: 0.9,
          }}
          minZoom={0.2}
          maxZoom={2}
          defaultViewport={{ x: 0, y: 0, zoom: 0.6 }}
        >
          <Background 
            color="#e0e7ff" 
            gap={24} 
            size={1.5}
            style={{ backgroundColor: '#faf5ff' }}
          />
          <Controls 
            className="!bg-white !shadow-xl !rounded-xl !border-2 !border-indigo-200 !overflow-hidden"
            showInteractive={false}
          />
          <MiniMap 
            nodeColor={(node) => {
              if (node.data.is_locked) return '#d1d5db';
              if (node.data.completed) return '#34d399';
              return '#818cf8';
            }}
            maskColor="rgba(99, 102, 241, 0.1)"
            className="!bg-white !shadow-xl !rounded-xl !border-2 !border-indigo-200"
            style={{ backgroundColor: 'white' }}
          />
        </ReactFlow>
        
        {/* Legend */}
        <div className="absolute top-28 left-8 bg-white rounded-2xl shadow-2xl border-2 border-indigo-200 p-5 z-10 backdrop-blur-sm bg-white/95">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1.5 h-6 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full"></div>
            <p className="text-sm font-bold text-gray-900">Module Status</p>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-400 rounded-lg shadow-md"></div>
              <div>
                <p className="text-sm text-gray-900 font-semibold">Completed</p>
                <p className="text-xs text-gray-500">You've finished this module</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-500 rounded-lg shadow-md"></div>
              <div>
                <p className="text-sm text-gray-900 font-semibold">Available</p>
                <p className="text-xs text-gray-500">Ready to start or in progress</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-white border-2 border-gray-300 rounded-lg shadow-sm opacity-60"></div>
              <div>
                <p className="text-sm text-gray-900 font-semibold">Locked</p>
                <p className="text-xs text-gray-500">Complete prerequisites first</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
